import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../layout/Layout';
import { organisationApi, securityCamerasApi } from '../../services/api';
import wsService from '../../services/websocket';
import { useDialogDraft } from '../../hooks/useDialogDraft';
import { buildDraftScopeKey, getDraftForScope, clearDraftForScope } from '../../utils/dialogDraftStore';
import './SecurityCamerasPage.css';

const tabs = [
  { id: 'cameras', label: 'Cameras', path: '/app/admin/security/cameras', enabled: true },
  { id: 'access-control', label: 'Access Control', enabled: false },
  { id: 'alarms', label: 'Alarms', enabled: false },
];

const VIEW_STORAGE_KEY = 'studioaeye-security-cameras-view';

function firstArray(...values) {
  return values.find((value) => Array.isArray(value)) || [];
}

function getApiErrorMessage(error) {
  return error?.response?.data?.message
    || error?.response?.data?.error?.message
    || error?.response?.data?.error
    || error?.message
    || 'Unable to load security cameras.';
}

function normalizeDevice(device = {}) {
  return {
    id: device.id,
    ownerOrgId: device.ownerOrgId ?? device.owner_org_id,
    hostname: device.hostname || `Device ${device.id}`,
    deviceType: device.deviceType ?? device.device_type ?? 'unknown',
    status: device.status || 'unknown',
    ipAddress: device.ipAddress ?? device.ip_address ?? '',
    macAddress: device.macAddress ?? device.mac_address ?? '',
    feedCount: Number(device.feedCount ?? device.feed_count ?? 0),
    lastSeen: device.lastSeen ?? device.last_seen ?? null,
  };
}

function normalizeFeed(feed = {}) {
  const device = feed.device || {};
  return {
    id: feed.id,
    ownerOrgId: feed.ownerOrgId ?? feed.owner_org_id,
    deviceId: feed.deviceId ?? feed.device_id,
    feedType: feed.feedType ?? feed.feed_type ?? 'camera',
    channelNumber: feed.channelNumber ?? feed.channel_number ?? null,
    name: feed.name || `Feed ${feed.id}`,
    streamUrl: feed.streamUrl ?? feed.stream_url ?? '',
    streamProtocol: feed.streamProtocol ?? feed.stream_protocol ?? '',
    resolution: feed.resolution || '',
    isOnline: Boolean(feed.isOnline ?? feed.is_online),
    frigateEnabled: Boolean(feed.frigateEnabled ?? feed.frigate_enabled),
    frigateConfig: feed.frigateConfig ?? feed.frigate_config ?? null,
    lastSeen: feed.lastSeen ?? feed.last_seen ?? null,
    device: {
      hostname: device.hostname ?? feed.device_hostname ?? '',
      type: device.type ?? feed.device_type ?? '',
      ipAddress: device.ipAddress ?? feed.device_ip ?? '',
      macAddress: device.macAddress ?? feed.device_mac ?? '',
    },
  };
}

function getFeedFromResponse(response) {
  return normalizeFeed(
    response.data?.data?.feed
    || response.data?.data
    || response.data?.feed
    || {},
  );
}

function formatDeviceType(type) {
  if (type === 'camera_gateway') return 'NVR';
  if (type === 'onvif_camera') return 'Camera';
  return type ? type.replace(/_/g, ' ') : 'Unknown';
}

function formatLastSeen(value) {
  if (!value) return 'Not seen';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.max(0, Math.round(diffMs / 60000));
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} h ago`;
  return date.toLocaleDateString();
}

function getGroupStatus(device, feeds) {
  if (feeds.some((feed) => feed.isOnline)) return 'online';
  return device.status || 'unknown';
}

function snapshotPayloadToDataUrl(payload = {}) {
  const image = payload.image || payload.imageBase64;
  if (!image) return '';
  if (String(image).startsWith('data:')) return image;
  const contentType = payload.contentType || payload.content_type || 'image/jpeg';
  return `data:${contentType};base64,${image}`;
}

function maskStreamUrl(value = '') {
  if (!value) return 'No stream URL';
  try {
    const parsed = new URL(value);
    if (parsed.username || parsed.password) {
      parsed.username = '***';
      parsed.password = '';
      return parsed.toString().replace('%2A%2A%2A', '***');
    }
    return `${parsed.protocol}//${parsed.host}${parsed.pathname || ''}`;
  } catch {
    return value.replace(/\/\/([^/@]+)@/, '//***@');
  }
}

function formatFrigateConfig(config) {
  if (!config || Object.keys(config).length === 0) {
    return '{\n  "detect": { "enabled": true },\n  "record": { "enabled": false },\n  "zones": {}\n}';
  }
  return JSON.stringify(config, null, 2);
}

const SecurityCameraThumbnailCard = ({
  feed,
  orgId,
  onOpen,
  refreshToken,
  snapshotUpdate,
}) => {
  const [visible, setVisible] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState('');
  const [snapshotCachedAt, setSnapshotCachedAt] = useState(null);
  const [snapshotError, setSnapshotError] = useState('');
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const cardRef = React.useRef(null);
  const objectUrlRef = React.useRef('');

  useEffect(() => {
    if (!feed.isOnline) {
      setVisible(true);
      return undefined;
    }

    const element = cardRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '160px' });

    observer.observe(element);
    return () => observer.disconnect();
  }, [feed.isOnline]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
  }, []);

  const loadSnapshot = useCallback(async () => {
    if (!orgId || !feed.isOnline || !visible) return;
    setSnapshotLoading(true);
    setSnapshotError('');

    try {
      const response = await securityCamerasApi.getSnapshot(feed.id, {
        org_id: orgId,
        width: 320,
        quality: 70,
      });
      const nextUrl = URL.createObjectURL(response.data);
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      objectUrlRef.current = nextUrl;
      setSnapshotUrl(nextUrl);
      setSnapshotCachedAt(response.headers?.['x-cached-at'] || new Date().toISOString());
    } catch (snapshotLoadError) {
      setSnapshotError(getApiErrorMessage(snapshotLoadError));
    } finally {
      setSnapshotLoading(false);
    }
  }, [feed.id, feed.isOnline, orgId, visible]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot, refreshToken]);

  useEffect(() => {
    if (!snapshotUpdate) return;
    const nextUrl = snapshotPayloadToDataUrl(snapshotUpdate);
    if (!nextUrl) return;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
    setSnapshotUrl(nextUrl);
    setSnapshotCachedAt(snapshotUpdate.cached_at || snapshotUpdate.cachedAt || new Date().toISOString());
    setSnapshotError('');
    setHighlight(true);
    const timer = setTimeout(() => setHighlight(false), 900);
    return () => clearTimeout(timer);
  }, [snapshotUpdate]);

  const openFeed = () => onOpen(feed);

  return (
    <article
      className={`security-thumbnail-card ${highlight ? 'updated' : ''}`}
      ref={cardRef}
      tabIndex={0}
      onClick={openFeed}
      onKeyDown={(event) => {
        if (event.key === 'Enter') openFeed();
      }}
      data-testid="security-camera-thumbnail-card"
    >
      <div className="security-thumbnail-frame">
        {feed.isOnline && snapshotUrl ? (
          <img src={snapshotUrl} alt={`${feed.name} snapshot`} data-testid="security-camera-thumbnail-image" />
        ) : feed.isOnline ? (
          <div className="security-thumbnail-placeholder" data-testid="security-camera-thumbnail-loading">
            {snapshotLoading ? 'Loading snapshot' : 'Snapshot unavailable'}
          </div>
        ) : (
          <div className="security-thumbnail-placeholder offline" data-testid="security-camera-thumbnail-offline">
            Offline
          </div>
        )}
        {snapshotCachedAt && (
          <span className="security-thumbnail-age" data-testid="security-camera-thumbnail-age">
            {formatLastSeen(snapshotCachedAt)}
          </span>
        )}
      </div>
      <div className="security-thumbnail-body">
        <strong data-testid="security-camera-thumbnail-name">{feed.name}</strong>
        <span data-testid="security-camera-thumbnail-meta">
          CH{feed.channelNumber || '-'} | {feed.resolution || 'No resolution'}
        </span>
        <div className="security-thumbnail-badges">
          <span className={`security-status-pill ${feed.isOnline ? 'online' : 'offline'}`}>
            {feed.isOnline ? 'Online' : 'Offline'}
          </span>
          {feed.frigateEnabled && <span className="security-frigate-badge">Frigate</span>}
        </div>
        {!feed.isOnline && <small>Last seen {formatLastSeen(feed.lastSeen)}</small>}
        {snapshotError && <small className="security-form-warning">{snapshotError}</small>}
      </div>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          loadSnapshot();
        }}
        disabled={!feed.isOnline || snapshotLoading}
        data-testid="security-camera-thumbnail-refresh"
      >
        Refresh
      </button>
    </article>
  );
};

const SecurityCameraFeedDetail = ({
  feed,
  orgId,
  snapshotUpdate,
  onBack,
  onSaved,
  onDeleted,
}) => {
  const [name, setName] = useState(feed.name);
  const [resolution, setResolution] = useState(feed.resolution || '');
  const [frigateEnabled, setFrigateEnabled] = useState(feed.frigateEnabled);
  const [frigateConfigText, setFrigateConfigText] = useState(formatFrigateConfig(feed.frigateConfig));
  const [streamRevealed, setStreamRevealed] = useState(false);
  const [snapshotUrl, setSnapshotUrl] = useState('');
  const [snapshotCachedAt, setSnapshotCachedAt] = useState(null);
  const [snapshotError, setSnapshotError] = useState('');
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingFrigate, setTogglingFrigate] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const objectUrlRef = React.useRef('');

  useEffect(() => {
    setName(feed.name);
    setResolution(feed.resolution || '');
    setFrigateEnabled(feed.frigateEnabled);
    setFrigateConfigText(formatFrigateConfig(feed.frigateConfig));
  }, [feed]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
  }, []);

  const loadSnapshot = useCallback(async () => {
    if (!orgId || !feed.isOnline) return;
    setSnapshotLoading(true);
    setSnapshotError('');
    try {
      const response = await securityCamerasApi.getSnapshot(feed.id, {
        org_id: orgId,
        width: 640,
        quality: 82,
      });
      const nextUrl = URL.createObjectURL(response.data);
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
      objectUrlRef.current = nextUrl;
      setSnapshotUrl(nextUrl);
      setSnapshotCachedAt(response.headers?.['x-cached-at'] || new Date().toISOString());
    } catch (snapshotLoadError) {
      setSnapshotError(getApiErrorMessage(snapshotLoadError));
    } finally {
      setSnapshotLoading(false);
    }
  }, [feed.id, feed.isOnline, orgId]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  useEffect(() => {
    if (!snapshotUpdate) return;
    const nextUrl = snapshotPayloadToDataUrl(snapshotUpdate);
    if (!nextUrl) return;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
    setSnapshotUrl(nextUrl);
    setSnapshotCachedAt(snapshotUpdate.cached_at || snapshotUpdate.cachedAt || new Date().toISOString());
    setSnapshotError('');
  }, [snapshotUpdate]);

  const saveChanges = async (event) => {
    event.preventDefault();
    if (!orgId) return;
    setSaving(true);
    setError('');
    setMessage('');

    let frigateConfig = null;
    if (frigateEnabled && frigateConfigText.trim()) {
      try {
        frigateConfig = JSON.parse(frigateConfigText);
      } catch {
        setError('Frigate config must be valid JSON.');
        setSaving(false);
        return;
      }
    }

    try {
      const response = await securityCamerasApi.updateFeed(feed.id, { org_id: orgId }, {
        name: name.trim(),
        resolution: resolution.trim() || null,
        frigate_enabled: frigateEnabled,
        frigate_config: frigateEnabled ? frigateConfig : null,
      });
      const updated = getFeedFromResponse(response);
      onSaved(updated);
      setMessage('Feed changes saved.');
    } catch (saveError) {
      setError(getApiErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  };

  const updateFrigateEnabled = async (checked) => {
    if (!orgId) return;
    const previous = frigateEnabled;
    setFrigateEnabled(checked);
    setTogglingFrigate(true);
    setError('');
    setMessage('');

    let frigateConfig = null;
    if (checked && frigateConfigText.trim()) {
      try {
        frigateConfig = JSON.parse(frigateConfigText);
      } catch {
        setFrigateEnabled(previous);
        setError('Frigate config must be valid JSON before enabling export.');
        setTogglingFrigate(false);
        return;
      }
    }

    try {
      const response = await securityCamerasApi.updateFeed(feed.id, { org_id: orgId }, {
        frigate_enabled: checked,
        frigate_config: checked ? frigateConfig : null,
      });
      const updated = getFeedFromResponse(response);
      onSaved(updated);
      setMessage(`Frigate export ${checked ? 'enabled' : 'disabled'}.`);
    } catch (toggleError) {
      setFrigateEnabled(previous);
      setError(getApiErrorMessage(toggleError));
    } finally {
      setTogglingFrigate(false);
    }
  };

  const confirmDelete = async () => {
    if (!orgId) return;
    setDeleting(true);
    setError('');
    try {
      await securityCamerasApi.deleteFeed(feed.id, { org_id: orgId });
      onDeleted(feed);
    } catch (deleteError) {
      setError(getApiErrorMessage(deleteError));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="security-feed-detail" data-testid="security-camera-feed-detail">
      <header className="security-feed-detail-header">
        <button type="button" onClick={onBack} data-testid="security-feed-detail-back">Back to Cameras</button>
        <div>
          <h2>{feed.name}</h2>
          <p>Channel {feed.channelNumber || '-'} | {feed.streamProtocol?.toUpperCase() || 'Stream'} | {feed.isOnline ? 'Online' : 'Offline'}</p>
        </div>
      </header>

      {message && <div className="security-cameras-notice" data-testid="security-feed-detail-notice">{message}</div>}
      {error && <div className="security-cameras-error" data-testid="security-feed-detail-error">{error}</div>}

      <section className="security-feed-detail-snapshot" data-testid="security-feed-detail-snapshot">
        {feed.isOnline && snapshotUrl ? (
          <img src={snapshotUrl} alt={`${feed.name} live snapshot`} data-testid="security-feed-detail-snapshot-image" />
        ) : feed.isOnline ? (
          <div className="security-thumbnail-placeholder" data-testid="security-feed-detail-snapshot-loading">
            {snapshotLoading ? 'Loading live snapshot' : 'Snapshot unavailable'}
          </div>
        ) : (
          <div className="security-thumbnail-placeholder offline" data-testid="security-feed-detail-snapshot-offline">Offline</div>
        )}
        {snapshotCachedAt && (
          <span className="security-thumbnail-age" data-testid="security-feed-detail-snapshot-age">
            {formatLastSeen(snapshotCachedAt)}
          </span>
        )}
        {snapshotError && <small className="security-form-warning">{snapshotError}</small>}
      </section>

      <form className="security-feed-detail-grid" onSubmit={saveChanges}>
        <section className="security-feed-detail-panel" data-testid="security-feed-detail-info">
          <h3>Feed Information</h3>
          <label>
            <span>Name</span>
            <input value={name} onChange={(event) => setName(event.target.value)} data-testid="security-feed-detail-name-input" />
          </label>
          <dl>
            <div><dt>Channel</dt><dd data-testid="security-feed-detail-channel">{feed.channelNumber || '-'}</dd></div>
            <div><dt>Protocol</dt><dd data-testid="security-feed-detail-protocol">{feed.streamProtocol?.toUpperCase() || '-'}</dd></div>
            <div>
              <dt>Resolution</dt>
              <dd>
                <input value={resolution} onChange={(event) => setResolution(event.target.value)} data-testid="security-feed-detail-resolution-input" />
              </dd>
            </div>
            <div><dt>Status</dt><dd data-testid="security-feed-detail-status">{feed.isOnline ? 'Online' : 'Offline'}</dd></div>
            <div><dt>Last seen</dt><dd data-testid="security-feed-detail-last-seen">{formatLastSeen(feed.lastSeen)}</dd></div>
            <div className="security-feed-detail-stream">
              <dt>Stream URL</dt>
              <dd>
                <code data-testid="security-feed-detail-stream-url">
                  {streamRevealed ? feed.streamUrl || 'No stream URL' : maskStreamUrl(feed.streamUrl)}
                </code>
                <button type="button" onClick={() => setStreamRevealed((current) => !current)} data-testid="security-feed-detail-stream-toggle">
                  {streamRevealed ? 'Hide' : 'Reveal'}
                </button>
              </dd>
            </div>
          </dl>
        </section>

        <section className="security-feed-detail-panel" data-testid="security-feed-detail-device">
          <h3>Device Information</h3>
          <dl>
            <div><dt>Device</dt><dd data-testid="security-feed-detail-device-hostname">{feed.device.hostname || 'Unknown device'}</dd></div>
            <div><dt>Type</dt><dd data-testid="security-feed-detail-device-type">{formatDeviceType(feed.device.type)}</dd></div>
            <div><dt>IP</dt><dd data-testid="security-feed-detail-device-ip">{feed.device.ipAddress || '-'}</dd></div>
            <div><dt>MAC</dt><dd data-testid="security-feed-detail-device-mac">{feed.device.macAddress || '-'}</dd></div>
          </dl>
        </section>

        <section className="security-feed-detail-panel security-feed-detail-frigate" data-testid="security-feed-detail-frigate">
          <h3>Frigate Configuration</h3>
          <label className="security-toggle">
            <input
              type="checkbox"
              checked={frigateEnabled}
              disabled={togglingFrigate}
              onChange={(event) => updateFrigateEnabled(event.target.checked)}
              data-testid="security-feed-detail-frigate-toggle"
            />
            <span>Enabled for Frigate export</span>
          </label>
          {frigateEnabled && (
            <label>
              <span>Raw JSON config</span>
              <textarea
                value={frigateConfigText}
                onChange={(event) => setFrigateConfigText(event.target.value)}
                rows={8}
                data-testid="security-feed-detail-frigate-json"
              />
            </label>
          )}
        </section>

        <footer className="security-feed-detail-actions">
          <button type="submit" disabled={saving} data-testid="security-feed-detail-save">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button type="button" className="danger" onClick={() => setDeleteOpen(true)} data-testid="security-feed-detail-delete">
            Delete Feed
          </button>
        </footer>
      </form>

      {deleteOpen && (
        <div className="security-feed-modal-backdrop" role="presentation" data-testid="security-feed-delete-dialog">
          <div className="security-feed-modal security-feed-delete-dialog" role="dialog" aria-modal="true" aria-labelledby="security-feed-delete-title">
            <header>
              <h2 id="security-feed-delete-title">Delete Feed</h2>
              <button type="button" onClick={() => setDeleteOpen(false)} aria-label="Close delete dialog">x</button>
            </header>
            <p>Delete {feed.name}? This removes the feed from the security camera list.</p>
            {feed.frigateEnabled && (
              <p className="security-form-warning" data-testid="security-feed-delete-frigate-warning">
                This feed is enabled for Frigate export. Delete it only after updating dependent Frigate configuration.
              </p>
            )}
            <footer>
              <button type="button" onClick={() => setDeleteOpen(false)}>Cancel</button>
              <button type="button" className="danger" disabled={deleting} onClick={confirmDelete} data-testid="security-feed-delete-confirm">
                {deleting ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

const SecurityCamerasPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { feedId } = useParams();
  const [orgId, setOrgId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [detailFeed, setDetailFeed] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [frigateFilter, setFrigateFilter] = useState('all');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_STORAGE_KEY) || 'list');
  const [updatingFeedId, setUpdatingFeedId] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [editingFeed, setEditingFeed] = useState(null);
  const [form, setForm] = useState({
    deviceId: '',
    name: '',
    channelNumber: '',
    streamUrl: '',
    streamProtocol: 'rtsp',
    resolution: '',
    frigateEnabled: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [savingFeed, setSavingFeed] = useState(false);
  const [thumbnailRefreshToken, setThumbnailRefreshToken] = useState(0);
  const [snapshotUpdates, setSnapshotUpdates] = useState({});
  // M004-F0613 / T-M004-0020: Add/Edit Feed is explicitly in scope for draft
  // persistence even though it was out of scope for the F0612 dismissal fix (this
  // dialog has no outside-click dismiss handler at all today, so it never suffered
  // the dismissal bug, but it can still lose typed data to an incidental close).
  const newFeedDraft = useDialogDraft('security-feed-new', 'new', { active: modalMode === 'add' });
  const editFeedDraft = useDialogDraft('security-feed-edit', editingFeed?.id, { active: modalMode === 'edit' });
  const feedDialogDraftRef = modalMode === 'edit' ? editFeedDraft.containerRef : newFeedDraft.containerRef;

  const loadCameras = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice('');

    try {
      const orgResponse = await organisationApi.getMine();
      const organisations = firstArray(
        orgResponse.data?.data?.organisations,
        orgResponse.data?.data,
        orgResponse.data?.organisations,
      );
      const nextOrgId = organisations.find((org) => org?.id)?.id;

      if (!nextOrgId) {
        setOrgId(null);
        setDevices([]);
        setFeeds([]);
        setExpandedGroups({});
        setLoading(false);
        return;
      }

      setOrgId(nextOrgId);
      const params = { org_id: nextOrgId };
      const [devicesResponse, feedsResponse] = await Promise.all([
        securityCamerasApi.getDevices(params),
        securityCamerasApi.getFeeds(params),
      ]);
      const nextDevices = firstArray(
        devicesResponse.data?.data?.devices,
        devicesResponse.data?.data,
        devicesResponse.data?.devices,
      ).map(normalizeDevice);
      const nextFeeds = firstArray(
        feedsResponse.data?.data?.feeds,
        feedsResponse.data?.data,
        feedsResponse.data?.feeds,
      ).map(normalizeFeed);

      setDevices(nextDevices);
      setFeeds(nextFeeds);
      setExpandedGroups((previous) => {
        const next = { ...previous };
        nextDevices.forEach((device) => {
          if (next[device.id] === undefined) next[device.id] = true;
        });
        nextFeeds.forEach((feed) => {
          if (next[feed.deviceId] === undefined) next[feed.deviceId] = true;
        });
        return next;
      });
    } catch (loadError) {
      setError(getApiErrorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCameras();
  }, [loadCameras]);

  useEffect(() => {
    if ((!feedId && viewMode !== 'thumbnails') || !orgId) return undefined;

    wsService.connect();
    const subscribeToOrg = () => wsService.subscribeToOrg(orgId);
    subscribeToOrg();

    const unsubscribeConnection = wsService.subscribe('connectionChange', ({ state }) => {
      if (state === 'connected') subscribeToOrg();
    });
    const unsubscribeSnapshot = wsService.subscribe('security:snapshot', (payload) => {
      const feedId = payload?.feedId ?? payload?.feed_id;
      if (!feedId) return;
      setSnapshotUpdates((current) => ({
        ...current,
        [feedId]: { ...payload, receivedAt: Date.now() },
      }));
    });

    return () => {
      unsubscribeConnection();
      unsubscribeSnapshot();
    };
  }, [feedId, orgId, viewMode]);

  useEffect(() => {
    if (!feedId || !orgId) {
      setDetailFeed(null);
      setDetailError('');
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    securityCamerasApi.getFeed(feedId, { org_id: orgId })
      .then((response) => {
        if (cancelled) return;
        setDetailFeed(getFeedFromResponse(response));
      })
      .catch((detailLoadError) => {
        if (cancelled) return;
        setDetailError(getApiErrorMessage(detailLoadError));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [feedId, orgId]);

  const groups = useMemo(() => {
    const byDevice = new Map();

    devices.forEach((device) => {
      byDevice.set(device.id, { device, feeds: [] });
    });

    feeds.forEach((feed) => {
      const existing = byDevice.get(feed.deviceId);
      if (existing) {
        existing.feeds.push(feed);
        return;
      }

      byDevice.set(feed.deviceId, {
        device: normalizeDevice({
          id: feed.deviceId,
          hostname: feed.device.hostname,
          deviceType: feed.device.type,
          ipAddress: feed.device.ipAddress,
          macAddress: feed.device.macAddress,
          status: feed.isOnline ? 'online' : 'unknown',
        }),
        feeds: [feed],
      });
    });

    const term = search.trim().toLowerCase();

    return Array.from(byDevice.values())
      .map((group) => ({
        ...group,
        feeds: group.feeds
          .slice()
          .sort((a, b) => (a.channelNumber || 999) - (b.channelNumber || 999) || a.name.localeCompare(b.name)),
      }))
      .filter(({ device, feeds: groupFeeds }) => {
        const deviceIsNvr = device.deviceType === 'camera_gateway';
        if (typeFilter === 'nvr' && !deviceIsNvr) return false;
        if (typeFilter === 'camera' && deviceIsNvr) return false;

        const status = getGroupStatus(device, groupFeeds);
        if (statusFilter === 'online' && status !== 'online') return false;
        if (statusFilter === 'offline' && status === 'online') return false;

        const filteredFeeds = groupFeeds.filter((feed) => {
          if (frigateFilter === 'enabled' && !feed.frigateEnabled) return false;
          if (frigateFilter === 'disabled' && feed.frigateEnabled) return false;
          if (statusFilter === 'online' && !feed.isOnline) return false;
          if (statusFilter === 'offline' && feed.isOnline) return false;
          if (!term) return true;
          return [
            feed.name,
            feed.resolution,
            feed.streamProtocol,
            device.hostname,
            device.ipAddress,
            device.macAddress,
          ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term));
        });

        if (groupFeeds.length === 0) {
          return !term && frigateFilter === 'all';
        }

        return filteredFeeds.length > 0;
      })
      .map((group) => {
        const term = search.trim().toLowerCase();
        const filteredFeeds = group.feeds.filter((feed) => {
          if (frigateFilter === 'enabled' && !feed.frigateEnabled) return false;
          if (frigateFilter === 'disabled' && feed.frigateEnabled) return false;
          if (statusFilter === 'online' && !feed.isOnline) return false;
          if (statusFilter === 'offline' && feed.isOnline) return false;
          if (!term) return true;
          return [
            feed.name,
            feed.resolution,
            feed.streamProtocol,
            group.device.hostname,
            group.device.ipAddress,
            group.device.macAddress,
          ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term));
        });
        return { ...group, feeds: filteredFeeds };
      });
  }, [devices, feeds, frigateFilter, search, statusFilter, typeFilter]);

  const toggleGroup = (deviceId) => {
    setExpandedGroups((previous) => ({ ...previous, [deviceId]: !previous[deviceId] }));
  };

  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_STORAGE_KEY, mode);
  };

  const openFeedDetail = (feed) => {
    navigate(`/app/admin/security/cameras/${feed.id}`, {
      state: { fromView: viewMode, filters: { search, typeFilter, statusFilter, frigateFilter } },
    });
  };

  const backToCameras = () => {
    const filters = location.state?.filters || {};
    if (location.state?.fromView) changeViewMode(location.state.fromView);
    if (filters.search !== undefined) setSearch(filters.search);
    if (filters.typeFilter !== undefined) setTypeFilter(filters.typeFilter);
    if (filters.statusFilter !== undefined) setStatusFilter(filters.statusFilter);
    if (filters.frigateFilter !== undefined) setFrigateFilter(filters.frigateFilter);
    navigate('/app/admin/security/cameras');
  };

  const handleDetailSaved = (updatedFeed) => {
    setDetailFeed(updatedFeed);
    setFeeds((current) => current.map((feed) => (
      String(feed.id) === String(updatedFeed.id) ? { ...feed, ...updatedFeed } : feed
    )));
  };

  const handleDetailDeleted = (deletedFeed) => {
    setFeeds((current) => current.filter((feed) => String(feed.id) !== String(deletedFeed.id)));
    navigate('/app/admin/security/cameras');
    setNotice(`${deletedFeed.name} deleted.`);
  };

  const handleFrigateToggle = async (feed, checked) => {
    if (!orgId) return;
    setUpdatingFeedId(feed.id);
    setError('');
    setNotice('');

    const previousFeeds = feeds;
    setFeeds((current) => current.map((item) => (
      item.id === feed.id ? { ...item, frigateEnabled: checked } : item
    )));

    try {
      const response = await securityCamerasApi.updateFeed(feed.id, { org_id: orgId }, { frigate_enabled: checked });
      const updated = normalizeFeed(response.data?.data?.feed || {});
      setFeeds((current) => current.map((item) => (item.id === feed.id ? { ...item, ...updated } : item)));
      setNotice(`${feed.name} Frigate export ${checked ? 'enabled' : 'disabled'}.`);
    } catch (toggleError) {
      setFeeds(previousFeeds);
      setError(getApiErrorMessage(toggleError));
    } finally {
      setUpdatingFeedId(null);
    }
  };

  const selectedDevice = useMemo(
    () => devices.find((device) => String(device.id) === String(form.deviceId)) || null,
    [devices, form.deviceId],
  );
  const selectedDeviceIsNvr = selectedDevice?.deviceType === 'camera_gateway';
  const duplicateChannel = useMemo(() => {
    if (!selectedDeviceIsNvr || !form.channelNumber) return false;
    return feeds.some((feed) => (
      String(feed.deviceId) === String(form.deviceId)
      && String(feed.channelNumber) === String(form.channelNumber)
      && String(feed.id) !== String(editingFeed?.id || '')
    ));
  }, [editingFeed, feeds, form.channelNumber, form.deviceId, selectedDeviceIsNvr]);

  const openAddModal = () => {
    setModalMode('add');
    setEditingFeed(null);
    const draft = getDraftForScope(buildDraftScopeKey('security-feed-new', 'new'));
    setForm({
      deviceId: devices[0]?.id ? String(devices[0].id) : '',
      name: '',
      channelNumber: '',
      streamUrl: '',
      streamProtocol: 'rtsp',
      resolution: '',
      frigateEnabled: false,
      ...draft,
    });
    setFormErrors({});
    setNotice('');
    setError('');
  };

  const openEditModal = (feed) => {
    setModalMode('edit');
    setEditingFeed(feed);
    const draft = getDraftForScope(buildDraftScopeKey('security-feed-edit', feed.id));
    setForm({
      deviceId: String(feed.deviceId),
      name: feed.name,
      channelNumber: feed.channelNumber ? String(feed.channelNumber) : '',
      streamUrl: feed.streamUrl || '',
      streamProtocol: feed.streamProtocol || 'rtsp',
      resolution: feed.resolution || '',
      frigateEnabled: feed.frigateEnabled,
      ...draft,
    });
    setFormErrors({});
    setNotice('');
    setError('');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingFeed(null);
    setFormErrors({});
    setSavingFeed(false);
  };

  const updateForm = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === 'deviceId') {
        const nextDevice = devices.find((device) => String(device.id) === String(value));
        if (nextDevice?.deviceType !== 'camera_gateway') {
          next.channelNumber = '';
        }
      }
      return next;
    });
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.deviceId) nextErrors.deviceId = 'Device is required.';
    if (!form.name.trim()) nextErrors.name = 'Name is required.';
    if (selectedDeviceIsNvr && !form.channelNumber) nextErrors.channelNumber = 'Channel number is required for NVR feeds.';
    if (!form.streamUrl.trim()) {
      nextErrors.streamUrl = 'Stream URL is required.';
    } else {
      try {
        const parsed = new URL(form.streamUrl);
        if (!['rtsp:', 'http:', 'https:'].includes(parsed.protocol)) {
          nextErrors.streamUrl = 'Use an RTSP, HTTP, or HTTPS stream URL.';
        }
      } catch {
        nextErrors.streamUrl = 'Enter a valid stream URL.';
      }
    }
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const saveFeed = async (event) => {
    event.preventDefault();
    if (!orgId || !validateForm()) return;
    setSavingFeed(true);
    setError('');
    setNotice('');
    // Captured before closeModal() resets editingFeed/modalMode to blank.
    const feedDraftScopeKey = modalMode === 'edit' && editingFeed
      ? buildDraftScopeKey('security-feed-edit', editingFeed.id)
      : buildDraftScopeKey('security-feed-new', 'new');

    const payload = {
      name: form.name.trim(),
      stream_url: form.streamUrl.trim(),
      stream_protocol: form.streamProtocol,
      resolution: form.resolution.trim() || null,
      frigate_enabled: form.frigateEnabled,
    };

    let successMessage = '';

    try {
      if (modalMode === 'edit' && editingFeed) {
        await securityCamerasApi.updateFeed(editingFeed.id, { org_id: orgId }, payload);
        successMessage = 'Feed updated.';
      } else {
        await securityCamerasApi.createFeed({
          ...payload,
          owner_org_id: orgId,
          device_id: Number(form.deviceId),
          feed_type: selectedDeviceIsNvr ? 'nvr_channel' : 'camera',
          channel_number: selectedDeviceIsNvr ? Number(form.channelNumber) : null,
        });
        successMessage = 'Feed created.';
      }
      closeModal();
      clearDraftForScope(feedDraftScopeKey);
      await loadCameras();
      setNotice(successMessage);
    } catch (saveError) {
      setFormErrors({ submit: getApiErrorMessage(saveError) });
    } finally {
      setSavingFeed(false);
    }
  };

  return (
    <Layout>
      <div className="security-page" data-testid="security-page">
        <header className="security-page-header">
          <div>
            <h1>Security</h1>
            <p>Building security devices, feeds, and monitoring surfaces.</p>
          </div>
        </header>

        <nav className="security-tabs" aria-label="Security sections" role="tablist" data-testid="security-tabs">
          {tabs.map((tab) => {
            const active = tab.enabled && location.pathname.startsWith(tab.path);

            if (!tab.enabled) {
              return (
                <button
                  key={tab.id}
                  type="button"
                  className="security-tab security-tab-disabled"
                  role="tab"
                  aria-selected="false"
                  aria-disabled="true"
                  disabled
                  title={`${tab.label} is not available yet`}
                  data-testid={`security-tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              );
            }

            return (
              <Link
                key={tab.id}
                to={tab.path}
                className={`security-tab ${active ? 'active' : ''}`}
                role="tab"
                aria-selected={active ? 'true' : 'false'}
                data-testid={`security-tab-${tab.id}`}
              >
                {tab.label}
              </Link>
            );
          })}
        </nav>

        <section className="security-cameras-shell" data-testid="security-cameras-tab-panel">
          <div className="security-cameras-toolbar" data-testid="security-cameras-toolbar">
            <label>
              <span>Search</span>
              <input
                type="search"
                placeholder="Search cameras, devices, or IPs"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                data-testid="security-cameras-search"
              />
            </label>
            <label>
              <span>Type</span>
              <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} data-testid="security-cameras-type-filter">
                <option value="all">All</option>
                <option value="camera">Camera</option>
                <option value="nvr">NVR</option>
              </select>
            </label>
            <label>
              <span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} data-testid="security-cameras-status-filter">
                <option value="all">All</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>
            </label>
            <label>
              <span>Frigate</span>
              <select value={frigateFilter} onChange={(event) => setFrigateFilter(event.target.value)} data-testid="security-cameras-frigate-filter">
                <option value="all">All</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
            <div className="security-view-toggle" data-testid="security-cameras-view-toggle">
              <button
                type="button"
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => changeViewMode('list')}
                data-testid="security-cameras-view-list"
                aria-pressed={viewMode === 'list'}
              >
                List
              </button>
              <button
                type="button"
                className={viewMode === 'thumbnails' ? 'active' : ''}
                onClick={() => changeViewMode('thumbnails')}
                data-testid="security-cameras-view-thumbnails"
                aria-pressed={viewMode === 'thumbnails'}
              >
                Thumbnails
              </button>
            </div>
            <button
              type="button"
              className="security-add-feed-button"
              onClick={openAddModal}
              data-testid="security-cameras-add-feed"
            >
              Add Feed
            </button>
            {viewMode === 'thumbnails' && (
              <button
                type="button"
                className="security-add-feed-button secondary"
                onClick={() => setThumbnailRefreshToken((current) => current + 1)}
                data-testid="security-cameras-refresh-all"
              >
                Refresh All
              </button>
            )}
          </div>

          {feedId ? (
            detailLoading || loading ? (
              <div className="security-cameras-state" data-testid="security-feed-detail-loading">Loading feed...</div>
            ) : detailError ? (
              <div className="security-cameras-error" data-testid="security-feed-detail-error">{detailError}</div>
            ) : detailFeed ? (
              <SecurityCameraFeedDetail
                feed={detailFeed}
                orgId={orgId}
                snapshotUpdate={snapshotUpdates[detailFeed.id]}
                onBack={backToCameras}
                onSaved={handleDetailSaved}
                onDeleted={handleDetailDeleted}
              />
            ) : (
              <div className="security-cameras-error" data-testid="security-feed-detail-error">Feed not found.</div>
            )
          ) : (
            <>
              {notice && <div className="security-cameras-notice" data-testid="security-cameras-notice">{notice}</div>}
              {error && <div className="security-cameras-error" data-testid="security-cameras-error">{error}</div>}

              {loading ? (
            <div className="security-cameras-state" data-testid="security-cameras-loading">Loading cameras...</div>
          ) : groups.length === 0 || feeds.length === 0 ? (
            <div className="security-cameras-empty" data-testid="security-cameras-empty">
              <h2>Cameras</h2>
              <p>No camera feeds match the current filters.</p>
              <button type="button" onClick={openAddModal} data-testid="security-cameras-empty-add-feed">Add Feed</button>
            </div>
          ) : viewMode === 'list' ? (
            <div className="security-camera-groups" data-testid="security-cameras-list">
              {groups.map(({ device, feeds: groupFeeds }) => {
                const expanded = expandedGroups[device.id] !== false;
                const status = getGroupStatus(device, groupFeeds);

                return (
                  <article className="security-camera-group" key={device.id} data-testid="security-camera-device-group">
                    <button
                      type="button"
                      className="security-camera-group-header"
                      onClick={() => toggleGroup(device.id)}
                      aria-expanded={expanded}
                      data-testid="security-camera-group-toggle"
                    >
                      <span className="security-camera-chevron">{expanded ? 'v' : '>'}</span>
                      <span className="security-camera-device-icon">{device.deviceType === 'camera_gateway' ? 'NVR' : 'CAM'}</span>
                      <span className="security-camera-device-main">
                        <strong data-testid="security-camera-device-hostname">{device.hostname}</strong>
                        <small>{device.ipAddress || 'No primary IP'}</small>
                      </span>
                      <span className={`security-status-pill ${status === 'online' ? 'online' : 'offline'}`}>{status}</span>
                      <span>{groupFeeds.length} {groupFeeds.length === 1 ? 'feed' : 'feeds'}</span>
                      <span className="security-device-type">{formatDeviceType(device.deviceType)}</span>
                    </button>

                    {expanded && (
                      <div className="security-feed-table-wrap">
                        <table className="security-feed-table">
                          <thead>
                            <tr>
                              <th>Channel</th>
                              <th>Name</th>
                              <th>Resolution</th>
                              <th>Online</th>
                              <th>Frigate</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupFeeds.map((feed) => (
                              <tr
                                key={feed.id}
                                tabIndex={0}
                                onClick={() => openFeedDetail(feed)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') openFeedDetail(feed);
                                }}
                                data-testid="security-camera-feed-row"
                              >
                                <td data-testid="security-camera-feed-channel">{feed.channelNumber || '-'}</td>
                                <td>
                                  <strong data-testid="security-camera-feed-name">{feed.name}</strong>
                                  <small>{feed.streamProtocol?.toUpperCase() || 'stream'} | {formatLastSeen(feed.lastSeen)}</small>
                                </td>
                                <td data-testid="security-camera-feed-resolution">{feed.resolution || '-'}</td>
                                <td>
                                  <span className={`security-status-pill ${feed.isOnline ? 'online' : 'offline'}`} data-testid="security-camera-feed-online">
                                    {feed.isOnline ? 'Online' : 'Offline'}
                                  </span>
                                </td>
                                <td>
                                  <label className="security-toggle" onClick={(event) => event.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={feed.frigateEnabled}
                                      disabled={updatingFeedId === feed.id}
                                      onChange={(event) => handleFrigateToggle(feed, event.target.checked)}
                                      data-testid="security-camera-frigate-toggle"
                                    />
                                    <span>{feed.frigateEnabled ? 'Enabled' : 'Disabled'}</span>
                                  </label>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openEditModal(feed);
                                    }}
                                    data-testid="security-camera-feed-edit"
                                  >
                                    Settings
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ) : viewMode === 'thumbnails' ? (
            <div className="security-thumbnail-groups" data-testid="security-cameras-thumbnails">
              {groups.map(({ device, feeds: groupFeeds }) => {
                const expanded = expandedGroups[device.id] !== false;
                if (groupFeeds.length === 0) return null;

                return (
                  <section className="security-thumbnail-group" key={device.id} data-testid="security-camera-thumbnail-device-group">
                    <button
                      type="button"
                      className="security-thumbnail-device-header"
                      onClick={() => toggleGroup(device.id)}
                      aria-expanded={expanded}
                      data-testid="security-camera-thumbnail-group-toggle"
                    >
                      <span>{expanded ? 'v' : '>'}</span>
                      <strong>{device.hostname}</strong>
                      <small>{device.ipAddress || 'No primary IP'} | {formatDeviceType(device.deviceType)}</small>
                      <span>{groupFeeds.length} {groupFeeds.length === 1 ? 'feed' : 'feeds'}</span>
                    </button>

                    {expanded && (
                      <div className="security-thumbnail-grid" data-testid="security-camera-thumbnail-grid">
                        {groupFeeds.map((feed) => (
                          <SecurityCameraThumbnailCard
                            key={feed.id}
                            feed={feed}
                            orgId={orgId}
                            onOpen={openFeedDetail}
                            refreshToken={thumbnailRefreshToken}
                            snapshotUpdate={snapshotUpdates[feed.id]}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="security-cameras-placeholder-grid" data-testid="security-cameras-thumbnail-placeholder">
              Select a camera view.
            </div>
          )}
            </>
          )}

          {!feedId && modalMode && (
            <div className="security-feed-modal-backdrop" role="presentation" data-testid="security-feed-modal-backdrop">
              <form className="security-feed-modal" ref={feedDialogDraftRef} onSubmit={saveFeed} data-testid="security-feed-modal">
                <header>
                  <h2>{modalMode === 'edit' ? 'Edit Feed' : 'Add Feed'}</h2>
                  <button type="button" onClick={closeModal} aria-label="Close feed dialog" data-testid="security-feed-modal-close">x</button>
                </header>

                <label>
                  <span>Device</span>
                  <select
                    name="deviceId"
                    value={form.deviceId}
                    onChange={(event) => updateForm('deviceId', event.target.value)}
                    disabled={modalMode === 'edit'}
                    data-testid="security-feed-device-select"
                  >
                    <option value="">Select device</option>
                    {devices.map((device) => (
                      <option key={device.id} value={device.id}>
                        {device.hostname} | {device.ipAddress || 'no IP'} | {formatDeviceType(device.deviceType)}
                        {device.feedCount ? ` | ${device.feedCount} feeds` : ''}
                      </option>
                    ))}
                  </select>
                  {formErrors.deviceId && <small className="security-form-error" data-testid="security-feed-device-error">{formErrors.deviceId}</small>}
                </label>

                <label>
                  <span>Name</span>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={(event) => updateForm('name', event.target.value)}
                    data-testid="security-feed-name-input"
                  />
                  {formErrors.name && <small className="security-form-error" data-testid="security-feed-name-error">{formErrors.name}</small>}
                </label>

                {selectedDeviceIsNvr && (
                  <label>
                    <span>Channel</span>
                    <input
                      type="number"
                      name="channelNumber"
                      min="1"
                      max="64"
                      value={form.channelNumber}
                      onChange={(event) => updateForm('channelNumber', event.target.value)}
                      data-testid="security-feed-channel-input"
                    />
                    {formErrors.channelNumber && <small className="security-form-error" data-testid="security-feed-channel-error">{formErrors.channelNumber}</small>}
                    {duplicateChannel && <small className="security-form-warning" data-testid="security-feed-duplicate-channel-warning">This channel already has a feed.</small>}
                  </label>
                )}

                <label>
                  <span>Stream URL</span>
                  <input
                    type="text"
                    name="streamUrl"
                    value={form.streamUrl}
                    onChange={(event) => updateForm('streamUrl', event.target.value)}
                    data-testid="security-feed-stream-url-input"
                    // Mantis #1177 / M004-D016 §6 (broadened): a stream URL can embed
                    // RTSP credentials (rtsp://user:pass@host/...). It is not
                    // type="password" and its name doesn't match the sensitive-name
                    // regex, so without this explicit exclusion it would be captured
                    // into the in-memory draft store. data-draft-exclude is the escape
                    // hatch M004-D016 §6 names for exactly this case; see
                    // SecurityCamerasDialogDraft.test.js for the mutation-proven
                    // regression test.
                    data-draft-exclude="true"
                  />
                  {formErrors.streamUrl && <small className="security-form-error" data-testid="security-feed-url-error">{formErrors.streamUrl}</small>}
                </label>

                <div className="security-feed-modal-grid">
                  <label>
                    <span>Protocol</span>
                    <select name="streamProtocol" value={form.streamProtocol} onChange={(event) => updateForm('streamProtocol', event.target.value)} data-testid="security-feed-protocol-select">
                      <option value="rtsp">RTSP</option>
                      <option value="http">HTTP</option>
                      <option value="onvif">ONVIF</option>
                    </select>
                  </label>
                  <label>
                    <span>Resolution</span>
                    <input
                      type="text"
                      name="resolution"
                      value={form.resolution}
                      onChange={(event) => updateForm('resolution', event.target.value)}
                      placeholder="1920x1080"
                      data-testid="security-feed-resolution-input"
                    />
                  </label>
                </div>

                <label className="security-toggle">
                  <input
                    type="checkbox"
                    name="frigateEnabled"
                    checked={form.frigateEnabled}
                    onChange={(event) => updateForm('frigateEnabled', event.target.checked)}
                    data-testid="security-feed-frigate-checkbox"
                  />
                  <span>Frigate export</span>
                </label>

                {formErrors.submit && <div className="security-cameras-error" data-testid="security-feed-submit-error">{formErrors.submit}</div>}

                <footer>
                  <button type="button" onClick={closeModal}>Cancel</button>
                  <button type="submit" disabled={savingFeed} data-testid="security-feed-save-button">
                    {savingFeed ? 'Saving...' : 'Save'}
                  </button>
                </footer>
              </form>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

export default SecurityCamerasPage;
